import Card from "../../common/Card";
import {
  apiCreateSiteFile,
  apiDeleteSite,
  apiDeleteSiteFile,
  apiGetSiteContacts,
  apiGetSiteDetails,
  apiGetSiteEvents,
  apiGetSiteFiles,
  apiGetSitePrestations,
  apiGetSiteProgressReports
} from "../../api/ApiSites";
import { useNavigate, useParams } from "react-router";
import { Content, ContentFixed, ContentScroll } from "../../common/Content";
import { HeaderMenu } from "../../common/HeaderMenu";
import { FabButton } from "../../common/FabButton";
import useFormat from "../../util/useFormat";
import styles from "./SiteDetails.module.scss";
import { useMutation, useQueries } from "@tanstack/react-query";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import SiteEditor from "./SiteEditor";
import { MODE_CREATE, MODE_EDIT } from "../../codes";
import { AppModal, DeleteModal } from "../../common/AppModal";
import SiteEventEditor from "./SiteEventEditor";
import useToast from "../../util/useToast";
import SitePrestationEditor from "./SitePrestationEditor";
import SiteContactsEditor from "./SiteContactsEditor";
import useAppContext from "../../useAppContext";
import { SiteStatusChip } from "../../common/SiteStatusChip";
import { formatDuration, round } from "../../util/util";
import moment from "moment";
import { useFileUploader } from "../../util/useFileUploader";
import { PhotoViewer } from "../../common/PhotoViewer";
import SiteProgressReportEditor from "./SiteProgressReportEditor";

const SiteDetails = () => {
  const { activeWorkers, getSiteStatus } = useAppContext();

  const navigate = useNavigate();
  const params = useParams();

  const { showToastSuccess } = useToast();

  const [showEditor, setShowEditor] = useState(false);
  const [eventEditor, setEventEditor] = useState({ show: false, event: {} });
  const [prestationEditor, setPrestationEditor] = useState({ show: false, prestation: {} });
  const [progressReportEditor, setProgressReportEditor] = useState({ show: false, progressReport: {} });
  const [contactsEditor, setContactsEditor] = useState({ show: false });
  const [deleteSiteModal, setDeleteSiteModal] = useState({ show: false });
  const [deleteFileModal, setDeleteFileModal] = useState({ show: false });
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [displayedProgressReport, setDisplayedProgressReport] = useState(null);

  const fileInputRef = useRef(null);
  const prestationsTableRef = useRef(null);

  const queries = useQueries({
    queries: [
      apiGetSiteDetails(params.id),
      apiGetSitePrestations(params.id),
      apiGetSiteContacts(params.id),
      apiGetSiteEvents(params.id),
      apiGetSiteFiles(params.id),
      apiGetSiteProgressReports(params.id),
    ]
  });

  const [siteQuery, prestationsQuery, contactsQuery, eventsQuery, filesQuery, progressReportsQuery] = queries;

  const site = siteQuery.data;
  const prestations = prestationsQuery.data || [];
  const contacts = contactsQuery.data || [];
  const events = eventsQuery.data || [];
  const progressReports = progressReportsQuery.data || [];
  const files = (filesQuery.data || []).filter(file => !file.pictureWidth);
  const photos = (filesQuery.data || []).filter(file => !!file.pictureWidth);

  const deleteSiteMutation = useMutation(apiDeleteSite());
  const createFileMutation = useMutation(apiCreateSiteFile(params.id));
  const deleteFileMutation = useMutation(apiDeleteSiteFile(params.id));

  const { uploadFiles, progress, total } = useFileUploader(
    (file, content) => createFileMutation.mutateAsync({ name: file.name, content })
  );

  if (queries.some(q => !q.data)) {
    return null;
  }

  const handleFileChange = async (event) => {
    setUploadingFiles(true);

    uploadFiles(event.target.files)
      .then(() => filesQuery.refetch())
      .finally(() => {
        event.target.value = "";

        setUploadingFiles(false);
      });
  };

  const deleteFile = file => {
    return deleteFileMutation.mutateAsync(file.id)
      .then(() => filesQuery.refetch())
      .then(() => {
        setDeleteFileModal({ show: false });

        showToastSuccess("Fichier supprimé");
      });
  };

  const openProgressReportPhoto = (e, progressReport) => {
    e.stopPropagation();

    setDisplayedProgressReport(progressReport);
  }

  const printPrestations = () => {
    const portal = document.getElementById("print-portal");

    const clone = prestationsTableRef.current.cloneNode(true);

    portal.innerHTML = ""; // Clear previous
    portal.appendChild(clone);

    document.body.classList.add("is-printing");

    window.print();

    document.body.classList.remove("is-printing");

    portal.innerHTML = "";
  };

  const siteColor = site.color.split("|");

  const actions = [
    {
      icon: "delete",
      label: "Supprimer le chantier",
      color: "danger",
      onClick: () => setDeleteSiteModal({ show: true })
    },
    {
      icon: "man",
      label: "Ajouter une prestation",
      onClick: () => setPrestationEditor({ show: true, prestation: {}, mode: MODE_CREATE })
    },
    {
      icon: "summarize",
      label: "Ajouter un relevé",
      onClick: () => setProgressReportEditor({ show: true, progressReport: {}, mode: MODE_CREATE })
    },
    {
      icon: "today",
      label: "Ajouter un événement",
      onClick: () => setEventEditor({ show: true, event: {}, mode: MODE_CREATE })
    },
    { icon: "upload_file", label: "Ajouter des fichiers", onClick: () => fileInputRef.current.click() },
    { icon: "groups", label: "Modifier les contacts", onClick: () => setContactsEditor({ show: true }) },
    { icon: "edit", label: "Modifier le chantier", onClick: () => setShowEditor(true) },
  ];

  const prestationsWorkers = {};
  const totalHoursByWorker = {};

  prestations
    .flatMap(date => date.workers)
    .forEach(worker => {
      prestationsWorkers[worker.workerId] = activeWorkers.find(w => w.id === worker.workerId);

      totalHoursByWorker[worker.workerId] = totalHoursByWorker[worker.workerId] || 0;
      totalHoursByWorker[worker.workerId] += worker.hours;
    });

  Object.keys(totalHoursByWorker).forEach(worker => totalHoursByWorker[worker] = round(totalHoursByWorker[worker], 2));

  return <Content>
    <ContentFixed>
      <HeaderMenu title={site.name} />
    </ContentFixed>

    <ContentScroll paddingTop>
      <div className={styles.siteLayout}>
        <Card padding title="Informations Générales" style={{
          border: "1px solid " + siteColor[0],
          borderLeft: "5px solid " + siteColor[0]
        }}>
          <LabelValue label="Nom">{site.name}</LabelValue>
          <LabelValue label="Adresse">{site.address || "-"}</LabelValue>
          <LabelValue label="Description">
            <div dangerouslySetInnerHTML={{ __html: (site.description) }} />
          </LabelValue>
          <LabelValue label="Statut"><SiteStatusChip status={getSiteStatus(site.status)} /></LabelValue>
        </Card>

        <Card title="Contacts">
          {contacts.length === 0 && <div className={styles.noContacts}>Pas de contacts</div>}
          {contacts.map(siteContact => <SiteContact key={siteContact.id} siteContact={siteContact} />)}
        </Card>

        <Card title={"Prestations (" + formatDuration(site.totalHours || 0) + ")"}
              titleActions={<div className={styles.printPrestationsTableIcon} onClick={printPrestations}>
                <span className="material-icons">print</span>
              </div>}
              className={styles.prestations}>
          <table className={styles.prestationsTable} ref={prestationsTableRef}>
            <thead>
            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td colSpan={Object.keys(prestationsWorkers).length} className={styles.hours}>Heures</td>
            </tr>
            <tr>
              <td className={styles.date}>Date</td>
              <td>Infos</td>
              <td>Relevé</td>
              {Object.values(prestationsWorkers)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(worker => <td key={worker.id} className={styles.worker}>{worker.name}</td>)}
            </tr>
            </thead>
            <tbody>
            {prestations
              .sort((a, b) => a.date.localeCompare(b.date))
              .map(prestation => {
                return <tr key={prestation.id}
                           onClick={() => setPrestationEditor({ show: true, prestation, mode: MODE_EDIT })}>
                  <td className={styles.date}>{moment(prestation.date).format("ddd DD/MM/YYYY")}</td>
                  <td dangerouslySetInnerHTML={{ __html: prestation.description }} />
                  <td dangerouslySetInnerHTML={{ __html: (prestation.progressReport) }} />

                  {Object.values(prestationsWorkers)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(worker => {
                      const workerHours = prestation.workers.find(w => w.workerId === worker.id) || { hours: 0 };

                      let workerComponent = "-";

                      if (workerHours.alert) {
                        workerComponent =
                          <div className="worker-alert"><span className="material-icons">error</span></div>;
                      } else if (workerHours.hours) {
                        workerComponent = formatDuration(workerHours.hours);
                      }

                      return <td key={worker.id}
                                 className={clsx(styles.worker, workerHours.hours > 0 ? null : styles.empty)}>
                        {workerComponent}
                      </td>;
                    })}
                </tr>;
              })}

            <tr className={styles.totalGeneralRow}>
              <td colSpan={3} className={styles.totalGeneral}>Total heures ({formatDuration(site.totalHours)})</td>

              {Object.values(prestationsWorkers)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(worker => {
                  return <td key={worker.id}
                             className={styles.workerTotal}>{formatDuration(totalHoursByWorker[worker.id])}</td>;
                })}
            </tr>
            </tbody>
          </table>
        </Card>

        {progressReports.length > 0 && <Card title="Relevés">
          <table className={styles.progressReportTable}>
            <thead>
            <tr>
              <td className={styles.date}>Date</td>
              <td className={styles.photo}>Photo</td>
              <td>Description</td>
            </tr>
            </thead>

            <tbody>
            {progressReports
              .sort((a, b) => a.date.localeCompare(b.date))
              .map(progressReport => {
                return <tr key={progressReport.id}
                           onClick={() => setProgressReportEditor({ show: true, progressReport, mode: MODE_EDIT })}>
                  <td className={styles.date}>{moment(progressReport.date).format("ddd DD/MM/YYYY")}</td>
                  <td className={styles.photo} onClick={e => openProgressReportPhoto(e, progressReport)}>
                    {progressReport.fileKey &&
                      <img
                        src={process.env.REACT_APP_API_URL + "/public/sipr/" + progressReport.fileKey + "?w=100&h=100"}
                        alt={progressReport.fileName} />
                    }
                  </td>
                  <td dangerouslySetInnerHTML={{ __html: (progressReport.description) }} />
                </tr>;
              })}
            </tbody>
          </table>

          <PhotoViewer
            photos={progressReports.filter(pr => !!pr.fileKey)}
            photo={displayedProgressReport}
            setPhoto={setDisplayedProgressReport}
            reloadPhotos={() => progressReportsQuery.refetch()}
            publicPath="sipr"
          />
        </Card>}

        <Card padding title="Événements">
          <SiteEvent date={site.dateStart} label="Début du chantier" />

          {events
            .sort((a, b) => a.date.localeCompare(b.date))
            .map(event => {
              return <SiteEvent
                key={event.id}
                date={event.date}
                label={event.name}
                description={event.description}
                editable
                onEdit={() => setEventEditor({ show: true, event, mode: MODE_EDIT })}
              />;
            })}

          {site.dateEnd && <SiteEvent date={site.dateEnd} label="Fin du chantier" />}
        </Card>

        {files.length === 0 && <Card title={"Fichiers"} padding>
          Pas de fichiers
        </Card>}

        {files.length > 0 && <Card title={"Fichiers (" + files.length + ")"}>
          {files.map(file => {
            const extension = file.name.split(".").pop()?.toLowerCase() || "unknown";

            return <div key={file.id} className={styles.siteFile}>
              <a className={styles.view}
                 href={process.env.REACT_APP_API_URL + "/public/sif/" + file.fileKey}
                 download={file.name}
                 target="_blank">
                <span className={clsx(styles.extension, styles[extension])}>{extension}</span>
                <span className={styles.name}>{file.name}</span>
              </a>

              <div className={styles.actions}>
                <span className={clsx("material-icons", styles.deleteIcon)}
                      onClick={() => setDeleteFileModal({ show: true, file })}>
                delete
                </span>
              </div>
            </div>;
          })}
        </Card>}

        <Photos photos={photos} filesQuery={filesQuery} />
      </div>
    </ContentScroll>

    <FabButton icon="more_vert" actions={actions} />

    <SiteEditor
      show={showEditor}
      hide={() => setShowEditor(false)}
      mode={MODE_EDIT}
      site={site}
      onSubmit={() => siteQuery.refetch()}
    />

    <SiteEventEditor
      show={eventEditor.show}
      hide={() => setEventEditor(prev => ({ ...prev, show: false }))}
      mode={eventEditor.mode}
      site={site}
      event={eventEditor.event}
      onSubmit={() => eventsQuery.refetch()}
      onDelete={() => eventsQuery.refetch()}
    />

    <SitePrestationEditor
      show={prestationEditor.show}
      hide={() => setPrestationEditor(prev => ({ ...prev, show: false }))}
      mode={prestationEditor.mode}
      site={site}
      prestation={prestationEditor.prestation}
      onSubmit={() => prestationsQuery.refetch()}
      onDelete={() => prestationsQuery.refetch()}
    />

    <SiteProgressReportEditor
      show={progressReportEditor.show}
      hide={() => setProgressReportEditor(prev => ({ ...prev, show: false }))}
      mode={progressReportEditor.mode}
      site={site}
      progressReport={progressReportEditor.progressReport}
      onSubmit={() => progressReportsQuery.refetch()}
      onDelete={() => progressReportsQuery.refetch()}
    />

    <SiteContactsEditor
      show={contactsEditor.show}
      hide={() => setContactsEditor({ show: false })}
      onSubmit={() => contactsQuery.refetch()}
      site={site}
      contacts={contacts}
    />

    <DeleteModal
      show={deleteSiteModal.show}
      title="Supprimer le chantier"
      onCancel={() => setDeleteSiteModal({ show: false })}
      onSubmit={() => {
        return deleteSiteMutation.mutateAsync(site.id).then(() => {
          showToastSuccess("Chantier supprimé");

          navigate("/sites");

          setDeleteSiteModal({ show: false });
        });
      }}
      cancelLabel="Annuler"
      submitLabel="Supprimer"
    >
      Etes-vous sûr de vouloir supprimer ce chantier ?
    </DeleteModal>

    <DeleteModal
      className={styles.deleteFileModal}
      size={"sm"}
      show={deleteFileModal.show}
      title="Supprimer le fichier"
      onCancel={() => setDeleteFileModal({ show: false })}
      onSubmit={() => deleteFile(deleteFileModal.file)}
      cancelLabel="Annuler"
      submitLabel="Supprimer"
    >
      Etes-vous sûr de vouloir supprimer ce fichier ?
    </DeleteModal>

    <AppModal
      size="sm"
      show={uploadingFiles}
      title="Envoi en cours"
    >
      <p>Veuillez patienter pendant l'envoi de vos fichiers ({progress} / {total}).</p>

      <div className={styles.fileProgressBar}>
        <div className={styles.fileProgressBarValue}></div>
      </div>
    </AppModal>

    <input
      className={styles.inputFile}
      type="file"
      ref={fileInputRef}
      name="files"
      multiple="multiple"
      onChange={handleFileChange}
    />
  </Content>;
};

const LabelValue = ({ label, children }) => {
  return <div className={styles.labelValue}>
    <div className={styles.label}>{label}</div>
    <div className={styles.value}>{children}</div>
  </div>;
};

const SiteContact = ({ siteContact }) => {
  const contactColor = siteContact.contact.color.split("|");

  return <div className={styles.contact}>
    <div className={styles.name}>{siteContact.contact.name}{siteContact.type && " (" + siteContact.type + ")"}</div>
    <div className={styles.contactCards} style={{ borderColor: contactColor[0] }}>
      {siteContact.contact.addresses
        .sort((a, b) => a.address.localeCompare(b.address))
        .map(address => (<div key={address.id}>{address.address}{address.label && " (" + address.label + ")"}</div>))}

      {siteContact.contact.phones
        .sort((a, b) => a.phone.localeCompare(b.phone))
        .map(phone => (<div key={phone.id}>{phone.phone}{phone.label && " (" + phone.label + ")"}</div>))}

      {siteContact.contact.emails
        .sort((a, b) => a.email.localeCompare(b.email))
        .map(email => (<div key={email.id}>{email.email}{email.label && " (" + email.label + ")"}</div>))}
    </div>
  </div>;
};

const SiteEvent = ({ date, label, description, editable, onEdit }) => {
  const { formatDate } = useFormat();

  return <div className={clsx(styles.siteEvent, editable ? styles.editable : null)} onClick={editable ? onEdit : null}>
    <div className={styles.timelineItem} />
    <div className={styles.eventDetails}>
      <div className={styles.date}>{formatDate(date)} | {label}</div>
      <div className={styles.label}>
        {description && <div className={styles.description} dangerouslySetInnerHTML={{ __html: (description) }} />}
      </div>
    </div>
  </div>;
};

const Photos = ({ photos, filesQuery }) => {
  const { formatDate } = useFormat();

  const params = useParams();

  const [photosPerRow, setPhotosPerRow] = useState(6);
  const [displayedPhoto, setDisplayedPhoto] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  const deletePhotoMutation = useMutation(apiDeleteSiteFile(params.id));

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 581) {
        setPhotosPerRow(3);
      } else if (width < 721) {
        setPhotosPerRow(4);
      } else if (width < 861) {
        setPhotosPerRow(5);
      } else {
        setPhotosPerRow(6);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const selectPhoto = photo => {
    setSelectedPhotos(prev => {
      if (prev.includes(photo.id)) {
        return prev.filter(id => id !== photo.id);
      } else {
        return [...prev, photo.id];
      }
    });
  };

  const groupedPhotos = {};

  for (const photo of photos) {
    const dateKey = photo.pictureDate.substring(0, 10);

    if (!groupedPhotos[dateKey]) {
      groupedPhotos[dateKey] = [];
    }

    groupedPhotos[dateKey].push(photo);
  }

  const sortedDates = Object.keys(groupedPhotos).sort((a, b) => {
    if (a === "unknown") {
      return 1;
    } else if (b === "unknown") {
      return -1;
    } else {
      return b.localeCompare(a);
    }
  });

  return <>
    {photos.length === 0 && <Card title={"Photos"} padding>
      Pas de photos
    </Card>}

    {photos.length > 0 &&
      <Card title={"Photos (" + photos.length + ")"} style={{ background: "none", border: "none" }}>
        {sortedDates.map(dateKey => {
          const groupPhotos = groupedPhotos[dateKey];
          const formattedDate = formatDate(dateKey);
          const remainder = groupPhotos.length % photosPerRow;
          const spacersCount = remainder === 0 ? 0 : photosPerRow - remainder;

          return (
            <div key={dateKey} className={styles.photoGroup}>
              <div className={styles.photoGroupHeader}>{formattedDate}</div>
              <div className={styles.sitePhotos} style={{ "--photos-per-row": photosPerRow }}>
                {groupPhotos.map(photo => {
                  return <div key={photo.id} className={styles.sitePhoto}>
                    {/*<span
                      className={clsx("material-icons", styles.checkIcon, selectedPhotos.indexOf(photo.id) !== -1 ? styles.selected : null)}
                      onClick={() => selectPhoto(photo)}
                    >check_circle</span>*/}

                    <span className={styles.infos} onClick={() => setDisplayedPhoto(photo)}>
                      <span className={styles.username}>{photo.createUserName}</span>
                      {photo.commentCount > 0 && <span className={styles.commentCount}>
                        {photo.commentCount} <span className="material-icons">chat</span>
                      </span>}
                    </span>

                    <img src={process.env.REACT_APP_API_URL + "/public/sip/" + photo.fileKey + "?w=200&h=200"}
                         onClick={() => setDisplayedPhoto(photo)}
                         alt={photo.name} />
                  </div>;
                })}

                {Array
                  .from({ length: spacersCount }, (_, x) => x)
                  .map(d => <div key={d} className={styles.sitePhoto}></div>)}
              </div>
            </div>
          );
        })}
      </Card>}

    <PhotoViewer
      photos={photos}
      photo={displayedPhoto}
      setPhoto={setDisplayedPhoto}
      reloadPhotos={() => filesQuery.refetch()}
      onDelete={deletePhotoMutation.mutateAsync}
      publicPath="sip"
    />
  </>;
};


export default SiteDetails;
